import type { DeviceStatic, Presence } from '../../realtime/types';

export interface DeviceFilter {
  /** case-insensitive substring of the name (the UID for imported devices) */
  q: string;
  protocol: string;
  /** allowed site ids (a site and its subtree), null = all */
  sites: ReadonlySet<string> | null;
  /** project id, '' = all */
  project: string;
  status: Presence | '';
  /** '' = all, 'direct' = own connection, 'via' = through any upstream device, else that upstream device's id */
  connectivity: string;
  /** incomplete data (user request 2026-11-02): '' = all */
  issue?: DeviceIssue | '';
}

/** Incomplete data of a device: no position (not on the map), GPS alarm, nothing heard for SILENT_AFTER_MS. */
export type DeviceIssue = 'no_position' | 'gps_problem' | 'silent';
export const DEVICE_ISSUES: DeviceIssue[] = ['no_position', 'gps_problem', 'silent'];
/** Lamps communicate every night: nothing for this long is a clear case, not a weekend outage. */
export const SILENT_AFTER_MS = 7 * 24 * 3600_000;

/**
 * Filters the scoped registry and counts what each protocol / status / issue option would give together with the other
 * active filters. Devices without live state count as `unknown`.
 */
export function filterDevices(
  all: readonly DeviceStatic[],
  f: DeviceFilter,
  presenceOf: (id: string) => Presence | undefined,
  issuesOf: (d: DeviceStatic) => readonly DeviceIssue[] = () => [],
) {
  const needle = f.q.trim().toLowerCase();
  const byProtocol = new Map<string, number>();
  const byStatus = new Map<Presence, number>();
  const byIssue = new Map<DeviceIssue, number>();
  const items: DeviceStatic[] = [];
  for (const d of all) {
    if (needle && !d.name.toLowerCase().includes(needle)) continue;
    if (f.sites && !f.sites.has(d.siteId)) continue;
    if (f.project && d.projectId !== f.project) continue;
    if (f.connectivity === 'direct' && d.upstream) continue;
    if (f.connectivity === 'via' && !d.upstream) continue;
    if (
      f.connectivity &&
      f.connectivity !== 'direct' &&
      f.connectivity !== 'via' &&
      d.upstream?.id !== f.connectivity
    )
      continue;
    const p = presenceOf(d.id) ?? 'unknown';
    const proto = d.protocol ?? '';
    const issues = issuesOf(d);
    const issueOk = !f.issue || issues.includes(f.issue);
    const protoOk = !f.protocol || proto === f.protocol;
    const statusOk = !f.status || p === f.status;
    if (statusOk && issueOk) byProtocol.set(proto, (byProtocol.get(proto) ?? 0) + 1);
    if (protoOk && issueOk) byStatus.set(p, (byStatus.get(p) ?? 0) + 1);
    if (protoOk && statusOk) for (const i of issues) byIssue.set(i, (byIssue.get(i) ?? 0) + 1);
    if (!protoOk || !statusOk || !issueOk) continue;
    items.push(d);
  }
  return { items, counts: { byProtocol, byStatus, byIssue } };
}
